// frontend/src/App.js - Simplified Final Version (No Extra Components Needed)
// Last updated: 2025-12-28 - Support ticket categories fix

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ROUTES } from './routes';

// Auth Provider
import { AuthProvider } from './contexts/AuthContext';

// 🎯 ADDED: Import DeliveryProvider for delivery agent operations
import { DeliveryProvider } from './contexts/DeliveryContext';

// Import auth utilities for debugging
import './utils/authUtils';

// Error Boundary Component
import ErrorBoundary from './components/ErrorBoundary';
import SimplePlacesTest from './components/SimplePlacesTest';

// Admin Auth Pages
import AdminLogin from './pages/admin/AdminLogin';

// Admin Dashboard Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ViewAllSellers from './pages/admin/ViewAllSellers';
import ViewSellerProfile from './pages/admin/ViewSellerProfile';
import ViewAllUsers from './pages/admin/ViewAllUsers';
import ViewUserProfile from './pages/admin/ViewUserProfile';
import DeliveryAgents from './pages/admin/DeliveryAgents';
import AdminPayoutDashboard from './pages/admin/PayoutDashboard';
import CODCollections from './pages/admin/CODCollections';
import LiveLogs from './pages/admin/LiveLogs';
import SupportTickets from './pages/admin/SupportTickets';
import SupportTicketDetail from './pages/admin/SupportTicketDetail';
import BannerManagement from './pages/admin/BannerManagement';
import PromoBannerManagement from './pages/admin/PromoBannerManagement';

// Admin Layout
import AdminLayout from './components/layouts/AdminLayout';
import { trackPageView } from './utils/analytics';
import SellerLayout from './components/layouts/SellerLayout';
import DeliveryLayout from './components/layouts/DeliveryLayout';

// Seller Auth Pages
import SellerLogin from './pages/auth/SellerLogin';
import SellerRegister from './pages/auth/SellerRegister';
import SellerForgotPassword from './pages/auth/SellerForgotPassword';
import SellerResetPassword from './pages/auth/SellerResetPassword';

// Seller Dashboard Pages
import SellerDashboard from './pages/seller/Dashboard';
import EditProduct from './pages/seller/EditProduct';
import EditProfile from './pages/seller/EditProfile';
import Orders from './pages/seller/Orders';
import PaymentTracking from './pages/seller/PaymentTracking';
import BeneficiaryManagement from './pages/seller/BeneficiaryManagement';
import PayoutDashboard from './pages/seller/PayoutDashboard';
import UploadCatalogue from './pages/seller/UploadCatalogue';
import MyCatalogues from './pages/seller/MyCatalogues';

// Delivery Agent Auth Pages
import DeliveryAgentLogin from './pages/auth/DeliveryAgentLogin';
import DeliveryAgentRegister from './pages/auth/DeliveryAgentRegister';
import DeliveryAgentForgotPassword from './pages/auth/DeliveryAgentForgotPassword';
import DeliveryAgentResetPassword from './pages/auth/DeliveryAgentResetPassword';

// Delivery Agent Dashboard Pages
import DeliveryDashboard from './pages/delivery/DeliveryDashboard';
import AvailableOrders from './pages/delivery/AvailableOrders';
import DeliveryHistory from './pages/delivery/DeliveryHistory';
import DeliveryProfile from './pages/delivery/DeliveryProfile';
import OrderDelivery from './pages/delivery/OrderDelivery';
import OrderPickup from './pages/delivery/OrderPickup';
import DeliveryEarnings from './pages/delivery/DeliveryEarnings';

// 🎯 ADDED: Import AssignedOrders component for "My Orders" functionality
import AssignedOrders from './pages/delivery/AssignedOrders';

// User Auth Pages
import UserLogin from './pages/auth/UserLogin';
import UserRegister from './pages/auth/UserRegister';
import UserForgotPassword from './pages/auth/UserForgotPassword';

// User Pages
import HomePage from './pages/user/HomePage';
import UserRouteWrapper from './components/layouts/UserRouteWrapper';
// UserDashboard deprecated — unified into HomePage
import ShopOffersPage from './pages/user/ShopOffersPage';
import CategoryPage from './pages/user/CategoryPage';
import HierarchicalCategoryPage from './pages/user/HierarchicalCategoryPage';
import CategoriesHomePage from './pages/user/CategoriesHomePage';
import ProductListPage from './pages/user/ProductListPage';
import ProductDetailPage from './pages/user/ProductDetailPage';
import ShopDetailPage from './pages/user/ShopDetailPage';
import ShopPage from './pages/user/ShopPage';
import NearbyShopsPage from './pages/user/NearbyShopsPage';
import CartPage from './pages/user/CartPage';
import CheckoutPage from './pages/user/CheckoutPage';
import PaymentPage from './pages/user/PaymentPage';
import PaymentReturnPage from './pages/user/PaymentReturnPage';
import WishlistPage from './pages/user/WishlistPage';
import TrendingPage from './pages/user/TrendingPage';
import LimitedEditionPage from './pages/user/LimitedEditionPage';
import OrderConfirmationPage from './pages/user/OrderConfirmationPage';
import CashfreePaymentConfirmationPage from './pages/user/CashfreePaymentConfirmationPage';
import UserProfile from './pages/user/UserProfile';
import ChangePassword from './pages/user/ChangePassword';
import MyOrdersPage from './pages/user/MyOrdersPage';

// Support Pages
import CreateTicket from './pages/support/CreateTicket';
import MyTickets from './pages/support/MyTickets';
import TicketDetail from './pages/support/TicketDetail';

// Static Pages
import AboutPage from './pages/user/AboutPage';
import ContactPage from './pages/user/ContactPage';
import HelpPage from './pages/user/HelpPage';

// Testing Components
import SocketTestComponent from './components/user/SocketTestComponent';

// Landing Page Component
import LandingPage from './components/LandingPage';

// Auth Modal
import { AuthModalProvider } from './contexts/AuthModalContext';
import AuthModal from './components/common/AuthModal';

// Policy Pages
import ContactPolicy from './pages/policy/ContactPolicy';
import ShippingPolicy from './pages/policy/ShippingPolicy';
import TermsPolicy from './pages/policy/TermsPolicy';
import RefundPolicy from './pages/policy/RefundPolicy';
import PrivacyPolicy from './pages/policy/PrivacyPolicy';

// Development error logging
const logRouteError = (error, errorInfo) => {
  console.error('Route Error:', error);
  console.error('Error Info:', errorInfo);
  
  // In production, you might want to send this to an error tracking service
  if (process.env.NODE_ENV === 'production') {
    // Example: Sentry.captureException(error);
  }
};

/**
 * Reports every client-side navigation to GA4.
 *
 * index.html sets send_page_view: false, so nothing — not even the first page —
 * is counted until this runs. That is deliberate: letting gtag auto-fire on
 * load and also firing here would double-count the landing page, while leaving
 * it on and not running this would miss every page after the first.
 *
 * Must live inside <Router>, since useLocation only works within it. Renders
 * nothing.
 */
const RouteAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Query strings can carry tokens and search terms, so only the path is sent.
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AuthModalProvider>
        <ErrorBoundary onError={logRouteError}>
        <div className="App">
            <ToastContainer 
              position="top-center"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          <AuthModal />
          <RouteAnalytics />
          <Routes>
            {/* ===== Admin + Seller Routes — full-width desktop layouts (AdminLayout / SellerLayout). */}
            {/* NOT wrapped in UserRouteWrapper, so they are not capped at the 480px MobileContainer width */}
            {/* and do not show the customer bottom nav. ===== */}

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
            <Route path="/admin/returns" element={<AdminLayout><AdminDashboard defaultActiveTab="returns" /></AdminLayout>} />
            <Route path="/admin/delivery-agents" element={<AdminLayout><DeliveryAgents /></AdminLayout>} />
            <Route path="/admin/users" element={<AdminLayout><ViewAllUsers /></AdminLayout>} />
            <Route path="/admin/sellers" element={<AdminLayout><ViewAllSellers /></AdminLayout>} />
            <Route path="/admin/sellers/:id" element={<AdminLayout><ViewSellerProfile /></AdminLayout>} />
            <Route path="/admin/users/:id" element={<AdminLayout><ViewUserProfile /></AdminLayout>} />
            <Route path="/admin/payouts" element={<AdminLayout><AdminPayoutDashboard /></AdminLayout>} />
            <Route path="/admin/cod-collections" element={<AdminLayout><CODCollections /></AdminLayout>} />
            <Route path="/admin/logs" element={<AdminLayout><LiveLogs /></AdminLayout>} />
            <Route path="/admin/live-logs" element={<AdminLayout><LiveLogs /></AdminLayout>} />
            <Route path="/admin/support/tickets" element={<AdminLayout><SupportTickets /></AdminLayout>} />
            <Route path="/admin/support/tickets/:ticketId" element={<AdminLayout><SupportTicketDetail /></AdminLayout>} />
            <Route path="/admin/banner-management" element={<AdminLayout><BannerManagement /></AdminLayout>} />
            <Route path="/admin/promo-banners" element={<AdminLayout><PromoBannerManagement /></AdminLayout>} />

            {/* 🆕 ADMIN ROUTE ALIASES - Point to main dashboard sections */}
            <Route path="/admin/orders" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
            <Route path="/admin/analytics" element={<AdminLayout><AdminDashboard /></AdminLayout>} />

            {/* Seller Support — the same three pages as the buyer flow, but inside
                SellerLayout. They already branch on which auth is active for their
                category list; previously the seller sidebar pointed at /support/tickets,
                which is a UserRouteWrapper route, so sellers landed in the buyer shell
                (480px frame, buyer bottom nav, no sidebar). */}
            <Route path="/seller/support/tickets" element={<SellerLayout><MyTickets /></SellerLayout>} />
            <Route path="/seller/support/create" element={<SellerLayout><CreateTicket /></SellerLayout>} />
            <Route path="/seller/support/tickets/:ticketId" element={<SellerLayout><TicketDetail /></SellerLayout>} />

            {/* Delivery Support — same fix; DeliveryLayout's sidebar pointed at the
                buyer route too. */}
            <Route path="/delivery/support/tickets" element={<DeliveryLayout><MyTickets /></DeliveryLayout>} />
            <Route path="/delivery/support/create" element={<DeliveryLayout><CreateTicket /></DeliveryLayout>} />
            <Route path="/delivery/support/tickets/:ticketId" element={<DeliveryLayout><TicketDetail /></DeliveryLayout>} />

            {/* Seller Auth Routes */}
            <Route path="/seller/login" element={<SellerLogin />} />
            <Route path="/seller/register" element={<SellerRegister />} />
            <Route path="/seller/forgot-password" element={<SellerForgotPassword />} />
            <Route path="/seller/reset-password/:token" element={<SellerResetPassword />} />

            {/* Seller Dashboard Routes */}
            <Route path="/seller/dashboard" element={<SellerDashboard />} />
            <Route path="/seller/upload-catalogue" element={<UploadCatalogue />} />
            <Route path="/seller/my-catalogues" element={<MyCatalogues />} />
            <Route path="/seller/edit-product/:id" element={<EditProduct />} />
            <Route path="/seller/edit-profile" element={<EditProfile />} />

            {/* Change 12 — "Add Product" and "View Products" duplicated Upload
                Catalogue and My Catalogues, so both were dropped from the nav.
                The paths stay as redirects so existing bookmarks and in-app
                links land somewhere useful instead of 404ing. */}
            <Route path="/seller/add-product" element={<Navigate replace to="/seller/upload-catalogue" />} />
            <Route path="/seller/view-products" element={<Navigate replace to="/seller/my-catalogues" />} />
            <Route path="/seller/orders" element={<Orders />} />
            <Route path="/seller/payment-tracking" element={<PaymentTracking />} />
            <Route path="/seller/beneficiary" element={<BeneficiaryManagement />} />
            <Route path="/seller/payouts" element={<PayoutDashboard />} />

            {/* Legacy route redirects for backward compatibility */}
            <Route path="/seller/products/add" element={<Navigate replace to="/seller/add-product" />} />
            <Route path="/seller/products/edit/:id" element={<Navigate replace to="/seller/edit-product/:id" />} />
            <Route path="/seller/products" element={<Navigate replace to="/seller/view-products" />} />
            <Route path="/seller/profile" element={<Navigate replace to="/seller/edit-profile" />} />

            {/* ===== Customer + Delivery Routes — wrapped in MobileContainer + customer Bottom Nav ===== */}
            <Route element={<UserRouteWrapper />}>
            <Route path="/" element={<HomePage />} />

            {/* Marketing Landing Page (moved from /) */}
            <Route path={ROUTES.LANDING} element={<LandingPage />} />
            
            {/* 🎯 DELIVERY AGENT ROUTES - Simple Approach with DeliveryProvider Only */}
            
            {/* Delivery Agent Auth Routes - Public (no protection needed) */}
            <Route path="/delivery/login" element={<DeliveryAgentLogin />} />
            <Route path="/delivery/register" element={<DeliveryAgentRegister />} />
            <Route path="/delivery/forgot-password" element={<DeliveryAgentForgotPassword />} />
            <Route path="/delivery/reset-password/:resetToken" element={<DeliveryAgentResetPassword />} />
            
            {/* 🎯 DELIVERY AGENT PROTECTED ROUTES - Simple approach using DeliveryProvider */}
            
            {/* Dashboard Route */}
            <Route path="/delivery/dashboard" element={
              <DeliveryProvider>
                <DeliveryDashboard />
              </DeliveryProvider>
            } />
            
            {/* 🆕 PROFILE ROUTE - NEW Addition */}
            <Route path="/delivery/profile" element={
              <DeliveryProvider>
                <DeliveryProfile />
              </DeliveryProvider>
            } />
            
            {/* Order Management Routes */}
            <Route path="/delivery/orders/available" element={
              <DeliveryProvider>
                <AvailableOrders />
              </DeliveryProvider>
            } />
            
            {/* 🆕 ASSIGNED ORDERS ROUTE - For "My Orders" functionality */}
            <Route path="/delivery/orders/assigned" element={
              <DeliveryProvider>
                <AssignedOrders />
              </DeliveryProvider>
            } />
            
            {/* Order Action Routes */}
            <Route path="/delivery/orders/:orderId/pickup" element={
              <DeliveryProvider>
                <OrderPickup />
              </DeliveryProvider>
            } />
            
            {/* Order Delivery Route */}
            <Route path="/delivery/orders/:orderId/delivery" element={
              <DeliveryProvider>
                <OrderDelivery />
              </DeliveryProvider>
            } />
            
            {/* Alternative delivery route for backward compatibility */}
            <Route path="/delivery/orders/:id/deliver" element={
              <DeliveryProvider>
                <OrderDelivery />
              </DeliveryProvider>
            } />
            
            {/* Delivery Earnings Route */}
            <Route path="/delivery/earnings" element={
              <DeliveryProvider>
                <DeliveryEarnings />
              </DeliveryProvider>
            } />
            
            {/* Delivery History Route */}
            <Route path="/delivery/history" element={
              <DeliveryProvider>
                <DeliveryHistory />
              </DeliveryProvider>
            } />
            
            {/* User Auth Routes */}
            <Route path="/user/login" element={<UserLogin />} />
            <Route path="/user/register" element={<UserRegister />} />
            {/* No /user/reset-password/:token route: UserForgotPassword handles
                the whole reset in one page (OTP, then the new password), so no
                link to a token URL was ever generated. The page that sat here
                never called the API — it reported success after a setTimeout. */}
            <Route path="/user/forgot-password" element={<UserForgotPassword />} />

            {/* User Pages */}
            <Route path="/user/dashboard" element={<Navigate replace to="/" />} />
            <Route path="/user/home" element={<Navigate replace to="/" />} />
            <Route path="/user/offers" element={<ShopOffersPage />} />

            {/* Categories Overview Page (NEW - fixes 404) */}
            <Route path="/user/categories" element={<CategoriesHomePage />} />

            {/* Legacy Category Route (Men/Women/Kids flat structure) */}
            <Route path="/user/categories/:category" element={<CategoryPage />} />

            {/* NEW: Hierarchical Category Browsing (4-Level Nested) */}
            <Route path="/user/browse" element={<HierarchicalCategoryPage />} />
            <Route path="/user/browse/:level1" element={<HierarchicalCategoryPage />} />
            <Route path="/user/browse/:level1/:level2" element={<HierarchicalCategoryPage />} />
            <Route path="/user/browse/:level1/:level2/:level3" element={<HierarchicalCategoryPage />} />

            <Route path="/user/products" element={<ProductListPage />} />
            <Route path="/user/product/:productId" element={<ProductDetailPage />} />
            <Route path="/user/shop/:shopId" element={<ShopDetailPage />} />

            {/* Change 15 — short, shareable aliases. The spec's sharing link is
                zammernow.com/product/<id>; the backend serves Open Graph tags
                on these paths so WhatsApp and friends can render a preview. */}
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/shop/:shopId" element={<ShopDetailPage />} />
            <Route path="/user/shop" element={<ShopPage />} />
            <Route path="/user/nearby-shops" element={<NearbyShopsPage />} />
            <Route path="/user/cart" element={<CartPage />} />
            <Route path="/user/checkout" element={<CheckoutPage />} />
            <Route path="/user/payment" element={<PaymentPage />} />
            <Route path="/payment-return" element={<PaymentReturnPage />} />
            <Route path="/payment/cashfree/confirmation/:orderId" element={<CashfreePaymentConfirmationPage />} />
            <Route path="/user/wishlist" element={<WishlistPage />} />
            <Route path="/user/trending" element={<TrendingPage />} />
            <Route path="/user/limited-edition" element={<LimitedEditionPage />} />
            <Route path="/user/order-confirmation" element={<OrderConfirmationPage />} />
            <Route path="/user/profile" element={<UserProfile />} />
            <Route path="/user/change-password" element={<ChangePassword />} />
            
            {/* User Order Routes */}
            <Route path="/user/orders" element={<MyOrdersPage />} />
            <Route path="/user/my-orders" element={<MyOrdersPage />} />
            
            {/* Support Routes */}
            <Route path="/support/create" element={<CreateTicket />} />
            <Route path="/support/tickets" element={<MyTickets />} />
            <Route path="/support/tickets/:ticketId" element={<TicketDetail />} />

            {/* Static Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/help" element={<HelpPage />} />

            {/* Policy Pages */}
            <Route path={ROUTES.POLICY_CONTACT} element={<ContactPolicy />} />
            <Route path={ROUTES.POLICY_SHIPPING} element={<ShippingPolicy />} />
            <Route path={ROUTES.POLICY_TERMS} element={<TermsPolicy />} />
            <Route path={ROUTES.POLICY_REFUND} element={<RefundPolicy />} />
            <Route path={ROUTES.POLICY_PRIVACY} element={<PrivacyPolicy />} />
            </Route>
            {/* END of UserRouteWrapper */}

            {/* Testing Routes */}
            <Route path="/test-socket" element={<SocketTestComponent />} />
            <Route path="/test-places" element={<SimplePlacesTest />} />
              
            {/* 404 Catch-all route */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                  <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                  <div className="space-x-4">
                    <a href="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                      Go Home
                    </a>
                    <a href="/user/login" className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors">
                      User Login
                    </a>
                    <a href="/delivery/login" className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors">
                      Delivery Login
                    </a>
                  </div>
                </div>
              </div>
            } />
          </Routes>
        </div>
        </ErrorBoundary>
        </AuthModalProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;